import React, { type JSX } from 'react';
import useStyles from './styles';
import { useTheme } from '@mui/material/styles';
import { Dialog, DialogTitle, Typography, DialogActions, DialogContent, Button } from '@mui/material';

import { CustomTransComponent } from 'components';
/**
 * The DiscardModalProps type is used in a TypeScript React component and includes properties for
 * visibility and a modal handler function.
 * @property {boolean} isVisible - The `isVisible` property is a boolean value that determines whether
 * the discard modal is visible or not. If `isVisible` is `true`, the modal is visible. If `isVisible`
 * is `false`, the modal is hidden.
 * @property modalHandler - The `modalHandler` property is a function that takes a boolean parameter
 * `action` and does something with it. It is used to handle the action performed on the modal, such as
 * closing it or confirming a discard action.
 */
type SubscriptionLimitModalProps = {
    isVisible: boolean;
    modalHandler: (action: boolean) => void;
    okHandler: () => void;
    deviceModal: string;
    // connectionString: string;
};

/**
 * The `DiscardModal` function is a TypeScript React component that renders a dialog box with a title,
 * message, and two buttons for canceling or discarding changes.
 * @param {SubscriptionLimitModalProps}  - - `isVisible`: a boolean value indicating whether the modal is visible
 * or not.
 * @returns The code is returning a JSX element, specifically a `Dialog` component with various child
 * components such as `DialogTitle`, `DialogContent`, and `DialogActions`.
 */
export const SubscriptionLimitModal = ({
    isVisible,
    modalHandler,
    deviceModal,
    okHandler,
}: SubscriptionLimitModalProps): JSX.Element => {
    const theme: any = useTheme();
    const classes = useStyles(theme);
    return (
        <Dialog
            open={isVisible}
            onClose={(): void => modalHandler(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                <Typography variant={'h6'}>
                    {' '}
                    {<CustomTransComponent translationKey={'ADD_DEVICE_FORM:DEVICE_LIMIT_TITLE'} />}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <div style={{ wordWrap: 'break-word' }}>
                    <Typography variant="body2" style={{ marginBottom: 10 }}>
                        {
                            <CustomTransComponent
                                translationKey={'ADD_DEVICE_FORM:DEVICE_LIMIT_MSG'}
                                replace={{ modelName: deviceModal }}
                            />
                        }
                    </Typography>
                </div>
            </DialogContent>
            <DialogActions className={classes.MuiDialogActionsRoot}>
                <Button
                    autoFocus
                    color="secondary"
                    sx={{
                        backgroundColor: theme?.palette?.primary?.main,
                        '&:hover': {
                            backgroundColor: theme?.palette?.primary?.main,
                        },
                    }}
                    variant="contained"
                    onClick={(): void => okHandler()}
                    data-testid="ok-action"
                >
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
};
